import React, { useMemo } from 'react';
import { StandardStatus } from '@zoocasa/go-search';
import { PropsWithTestId } from 'types/testId';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { isSold } from 'types/reso';
import { ListingDisplayPolicy, PricePresentation } from 'domain/price';
import Price from './index';

type PriceContainerProps = PropsWithTestId<{
  standardStatus: StandardStatus;
  price: number | null;
  soldPrice: number | null;
  province?: string | null;
  displayPolicy?: ListingDisplayPolicy;
}>;

/**
 * Container component that adapts listing data to PricePresentation.
 * Handles display policy creation, presentation model construction,
 * currency formatting, and localization.
 */
export default function PriceContainer({ 
  standardStatus, 
  price, 
  soldPrice, 
  province, 
  displayPolicy: providedPolicy, 
  testId, 
}: PriceContainerProps) {
  const { formatCurrency } = useI18nFormatting();
  const { t } = useTranslation(Namespaces.COMMON);
  
  // Create presentation model from domain logic
  const presentation = useMemo(() => {
    const isListingSold = isSold(standardStatus);
    const policy = providedPolicy ?? ListingDisplayPolicy.fromListing(province, isListingSold);
    
    return PricePresentation.fromListing(
      standardStatus, 
      price, 
      soldPrice, 
      policy, 
      formatCurrency
    );
  }, [standardStatus, price, soldPrice, province, providedPolicy, formatCurrency]);

  // Format original list price if needed
  const originalListPrice = price && price > 0 ? formatCurrency(price) : undefined;
  
  // Get localized list price prefix
  const listPricePrefix = t(LOCALE_COMMON_PATHS.LISTING_CARD_PRICE_LIST_PREFIX, {
    defaultValue: 'List: ',
  });

  return (
    <Price 
      presentation={presentation} 
      originalListPrice={originalListPrice} 
      listPricePrefix={listPricePrefix}
      testId={testId} 
    />
  );
}
